import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../ui/utils';
import type { DerivationStep } from './organHealth';

/** @deprecated use DerivationStep from organHealth. Kept for back-compat. */
export type ProvenanceStep = DerivationStep;

/**
 * The methodology's worked example (value → curve → sub-score → penalty →
 * cap → band) rendered as UI: a step table, not prose. Built inline-expand
 * (reusing the same disclosure convention as `NarrativeToggle` elsewhere in
 * this codebase) as the safe default — SPEC.md explicitly leaves
 * inline-expand vs. side-panel vs. modal as an open UX-research question
 * ("depends on whether clinicians open this once during onboarding or on
 * every score — that's a user research question, not a design one"). This
 * is a placeholder decision, not a resolved one; revisit with real usage
 * data before assuming it's settled.
 */
export function ScoreProvenance({ steps, formula }: { steps: DerivationStep[]; formula?: string | null }) {
  const [open, setOpen] = useState(false);
  if (steps.length === 0) return null;

  return (
    <div className="border-t border-border-subtle pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-1 rounded-sm text-xs font-medium text-brand-text',
          'hover:underline',
          // Keyboard users must be able to see where they are; the control is
          // already a semantic button with aria-expanded, but without a visible
          // focus ring the disclosure is reachable and invisible.
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-text/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        )}
      >
        {open ? <ChevronDown className="size-3.5" aria-hidden /> : <ChevronRight className="size-3.5" aria-hidden />}
        {open ? 'Hide derivation' : 'Show how this score was reached'}
      </button>
      {open ? (
        <div className="mt-2 overflow-hidden rounded-lg border border-border-subtle bg-surface-sunken/20">
          <div className="border-b border-border-subtle px-3 py-1.5">
            <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-text-tertiary">
              How the score was reached
            </span>
          </div>
          <ol>
            {steps.map((s, i) => (
              <li
                key={i}
                className={cn(
                  'flex items-start justify-between gap-3 px-3 py-2',
                  i > 0 && 'border-t border-border-subtle',
                  s.emphasis === 'cap' && 'border-l-2 border-l-(--capped-rule) bg-band-critical-bg/30',
                  s.emphasis === 'band' && 'bg-surface-sunken/50',
                  s.muted && 'opacity-70',
                )}
              >
                <div className="min-w-0">
                  <div
                    className={cn(
                      'text-[13px] font-medium tabular-nums',
                      s.emphasis === 'cap' ? 'text-band-critical-text' : s.muted ? 'text-text-tertiary' : 'text-text-primary',
                    )}
                  >
                    {s.step}
                  </div>
                  <div className="mt-0.5 text-xs leading-snug text-text-tertiary">{s.detail}</div>
                </div>
                <div
                  className={cn(
                    'shrink-0 pt-px text-sm font-medium tabular-nums',
                    s.emphasis === 'cap' ? 'text-band-critical-text' : s.muted ? 'text-text-tertiary' : 'text-text-primary',
                    s.emphasis === 'band' && 'text-[15px]',
                  )}
                >
                  {s.emphasis === 'band' ? <span className="text-text-tertiary">→ </span> : null}
                  {s.result}
                </div>
              </li>
            ))}
          </ol>
          {formula ? (
            <div className="border-t border-border-subtle bg-surface-sunken/40 px-3 py-2">
              <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-text-tertiary">Formula</span>
              <p className="mt-0.5 font-mono text-[11px] leading-snug tabular-nums text-text-secondary">{formula}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
